import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@core/lib/cn';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  className?: string;
}

/**
 * Native checkbox, styled but not replaced.
 *
 * The whole row is the label, so the tap target is the full width and at least
 * 44px tall — hitting a bare 16px box with a thumb is a coin flip. `accent-brand`
 * gets us a brand-coloured native control, which keeps the indeterminate state,
 * the platform's own focus behaviour and forced-colors mode working for free.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label
        htmlFor={fieldId}
        className={cn(
          'flex min-h-touch cursor-pointer items-start gap-3 py-2 text-base text-ink',
          rest.disabled && 'cursor-not-allowed text-ink-subtle',
        )}
      >
        <input
          {...rest}
          ref={ref}
          id={fieldId}
          type="checkbox"
          aria-describedby={describedBy || undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            'mt-0.5 h-6 w-6 shrink-0 rounded border-line-strong accent-brand',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2',
            error && 'border-danger',
          )}
        />
        <span className="pt-0.5">{label}</span>
      </label>

      {hint ? (
        <p id={hintId} className="pl-9 text-sm text-ink-subtle">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="pl-9 text-sm font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
});
